/**
 * SuggestionHistory Component
 *
 * Displays the history of checklist suggestion decisions (accepted/rejected)
 * for a document. Shows two sections: accepted (always visible) and rejected
 * (collapsible, collapsed by default). Users can change decisions post-confirm.
 */

import { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import type { FunctionReturnType } from "convex/server";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";
import { useApiMutation } from "@/packages/features-shared";
import { Button } from "@/components/ui/primitives";
import { useTranslation } from "@/packages/utils";

interface SuggestionHistoryProps {
  documentId: Id<"documents">;
}

type HistoryEntry = FunctionReturnType<
  typeof api.document_queries.getSuggestionHistory
>[number];

interface CategoryGroup {
  categoryId: string;
  categoryName: string;
  auditName: string;
  entries: HistoryEntry[];
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const colorMap: Record<string, string> = {
    high: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
    medium: "bg-amber-500/15 text-amber-400 border-amber-500/20",
    low: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  };
  const colors = colorMap[confidence] ?? colorMap.low;

  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${colors}`}
    >
      {confidence}
    </span>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return null;

  const colorMap: Record<string, string> = {
    MISSING: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
    UPLOADED: "bg-blue-500/15 text-blue-400 border-blue-500/20",
    APPROVED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
    REJECTED: "bg-red-500/15 text-red-400 border-red-500/20",
  };
  const colors = colorMap[status] ?? colorMap.MISSING;

  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${colors}`}
    >
      {status}
    </span>
  );
}

function groupByCategory(entries: HistoryEntry[]): CategoryGroup[] {
  const groupMap = new Map<string, CategoryGroup>();
  for (const entry of entries) {
    const key = entry.categoryId ?? "uncategorized";
    const existing = groupMap.get(key);
    if (existing) {
      existing.entries.push(entry);
    } else {
      groupMap.set(key, {
        categoryId: key,
        categoryName: entry.categoryName,
        auditName: entry.auditName,
        entries: [entry],
      });
    }
  }
  return [...groupMap.values()];
}

export function SuggestionHistory({ documentId }: SuggestionHistoryProps) {
  const { t } = useTranslation("documents");
  const workspaceId = useWorkspaceId();
  const [rejectedExpanded, setRejectedExpanded] = useState(false);

  const { data: historyData } = useWorkspaceQuery(
    api.document_queries.getSuggestionHistory,
    { documentId },
  );

  const acceptMutation = useApiMutation(
    api.document_mutations.acceptRejectedSuggestion,
    { name: "accept-rejected-suggestion" },
  );

  const unlinkMutation = useApiMutation(
    api.document_mutations.unlinkAcceptedSuggestion,
    { name: "unlink-accepted-suggestion" },
  );

  const accepted = useMemo(
    () => (historyData ?? []).filter((h) => h.decision === "accepted"),
    [historyData],
  );

  const rejected = useMemo(
    () => (historyData ?? []).filter((h) => h.decision === "rejected"),
    [historyData],
  );

  const acceptedGroups = useMemo(() => groupByCategory(accepted), [accepted]);
  const rejectedGroups = useMemo(() => groupByCategory(rejected), [rejected]);

  const handleAccept = async (entry: HistoryEntry) => {
    if (!entry.auditId) return;
    await acceptMutation.mutateAsync({
      documentId,
      checklistItemId: entry.checklistItemId,
      auditId: entry.auditId,
      workspaceId,
    });
  };

  const handleUnlink = async (entry: HistoryEntry) => {
    await unlinkMutation.mutateAsync({
      documentId,
      checklistItemId: entry.checklistItemId,
      workspaceId,
    });
  };

  if (!historyData) return null;

  return (
    <div className="space-y-5">
      {/* Accepted section - always visible */}
      <div className="space-y-2">
        <p className="text-[11px] uppercase text-muted font-medium tracking-wider">
          {t("detail.suggestions.history.acceptedTitle")} ({accepted.length})
        </p>

        {accepted.length === 0 ? (
          <p className="text-[12px] text-muted py-3 text-center">
            {t("detail.suggestions.history.noAccepted")}
          </p>
        ) : (
          <div className="space-y-3">
            {acceptedGroups.map((group) => (
              <div key={group.categoryId}>
                <div className="flex items-center gap-2 mb-1.5 px-0.5">
                  <p className="text-[11px] font-medium text-secondary">
                    {group.categoryName}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/15">
                    {group.auditName}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {group.entries.map((entry) => (
                    <div
                      key={entry._id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card"
                    >
                      <p className="text-[13px] text-primary font-medium truncate flex-1">
                        {entry.title}
                      </p>
                      <ConfidenceBadge confidence={entry.confidence} />
                      <StatusBadge status={entry.status} />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlink(entry)}
                        disabled={unlinkMutation.isPending}
                        className="text-[11px] text-muted hover:text-destructive shrink-0"
                      >
                        {unlinkMutation.isPending ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          t("detail.suggestions.history.unlink")
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rejected section - collapsible, hidden if empty */}
      {rejected.length > 0 && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setRejectedExpanded((prev) => !prev)}
            className="flex items-center gap-1.5 cursor-pointer group"
          >
            {rejectedExpanded ? (
              <ChevronDown size={14} className="text-muted" />
            ) : (
              <ChevronRight size={14} className="text-muted" />
            )}
            <p className="text-[11px] uppercase text-muted font-medium tracking-wider group-hover:text-secondary transition-colors">
              {t("detail.suggestions.history.rejectedTitle")} ({rejected.length}
              )
            </p>
          </button>

          {rejectedExpanded && (
            <div className="space-y-3">
              {rejectedGroups.map((group) => (
                <div key={group.categoryId}>
                  <div className="flex items-center gap-2 mb-1.5 px-0.5">
                    <p className="text-[11px] font-medium text-secondary">
                      {group.categoryName}
                    </p>
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/15">
                      {group.auditName}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {group.entries.map((entry) => (
                      <div
                        key={entry._id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card"
                      >
                        <p className="text-[13px] text-primary font-medium truncate flex-1">
                          {entry.title}
                        </p>
                        <ConfidenceBadge confidence={entry.confidence} />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAccept(entry)}
                          disabled={acceptMutation.isPending || !entry.auditId}
                          className="text-[11px] text-accent hover:text-accent/80 shrink-0"
                        >
                          {acceptMutation.isPending ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            t("detail.suggestions.history.accept")
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
